import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { expiredResetPasswordErrors, resetPasswordErrorMessages } from '@pages/reset-password/constants/reset-password-errors.constant';
import { PasswordPolicy } from '@pages/reset-password/models/password-policy.interface';
import { ResetPasswordCodeResponse } from '@pages/reset-password/models/reset-password-code-response.interface';
import { ResetPasswordError } from '@pages/reset-password/models/reset-password-error.enum';
import { ResetPasswordStep } from '@pages/reset-password/models/reset-password-step.enum';
import { ResetPasswordService } from '@pages/reset-password/services/reset-password.service';
import { ResetPassword, ValidateCode } from '@pages/reset-password/store/actions/reset-password.actions';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

interface ResetPasswordStateModel {
  isLoading: boolean;
  currentStep?: ResetPasswordStep;
  minimumPasswordLength?: number;
  errorMessageTranslationKey?: string;
}

@State<ResetPasswordStateModel>({
  name: 'resetPassword',
  defaults: {
    isLoading: false,
  },
})
@Injectable()
export class ResetPasswordState {
  private readonly service = inject(ResetPasswordService);
  private readonly authorizationService = inject(AuthorizationService);

  @Selector()
  static isLoading(state: ResetPasswordStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static currentStep(state: ResetPasswordStateModel): ResetPasswordStep | undefined {
    return state.currentStep;
  }

  @Selector()
  static minimumPasswordLength(state: ResetPasswordStateModel): number | undefined {
    return state.minimumPasswordLength;
  }

  @Selector()
  static errorMessageTranslationKey(state: ResetPasswordStateModel): string | undefined {
    return state.errorMessageTranslationKey;
  }

  @Action(ValidateCode)
  validateCode(context: StateContext<ResetPasswordStateModel>, { passwordCodeKey }: ValidateCode): Observable<PasswordPolicy> {
    context.patchState({ isLoading: true, errorMessageTranslationKey: undefined });

    return this.service.validateCode(passwordCodeKey).pipe(
      tap(({ minimumPasswordLength }) => {
        if (this.authorizationService.isAuthorized()) {
          this.authorizationService.logOut();
        }

        context.patchState({ minimumPasswordLength, currentStep: ResetPasswordStep.Reset });
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse?.status === HttpStatusCode.BadRequest) {
          context.patchState({ currentStep: ResetPasswordStep.Expired });
          return EMPTY;
        }

        context.patchState({ currentStep: ResetPasswordStep.ConnectionError });

        return EMPTY;
      }),
      finalize(() => context.patchState({ isLoading: false }))
    );
  }

  @Action(ResetPassword)
  resetPassword(context: StateContext<ResetPasswordStateModel>, { request }: ResetPassword): Observable<ResetPasswordCodeResponse> {
    context.patchState({ isLoading: true, errorMessageTranslationKey: undefined });

    return this.service.resetPassword(request).pipe(
      tap(() => {
        context.patchState({ currentStep: ResetPasswordStep.Success });
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse?.status !== HttpStatusCode.BadRequest) {
          context.patchState({ currentStep: ResetPasswordStep.ConnectionError });
          return EMPTY;
        }

        const errorCode = errorResponse.error;

        if (!this.isResetPasswordError(errorCode)) {
          return EMPTY;
        }

        if (expiredResetPasswordErrors.includes(errorCode)) {
          context.patchState({ currentStep: ResetPasswordStep.Expired });
        }

        const errorMessageTranslationKey = resetPasswordErrorMessages[errorCode];
        if (errorMessageTranslationKey) {
          context.patchState({ errorMessageTranslationKey });
        }

        return EMPTY;
      }),
      finalize(() => context.patchState({ isLoading: false }))
    );
  }

  private isResetPasswordError(error: unknown): error is ResetPasswordError {
    return Object.values(ResetPasswordError).includes(error as ResetPasswordError);
  }
}
